import { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Source } from '@store';
import { composeClassName } from '@utilities';

import { ContentMapperFunctions } from './types';

import styles from './index.module.scss';

type Props<T = Source> = {
	data: T;
	testId: string;
	contentMappers: ContentMapperFunctions<T>;
	selected: boolean;
	renderDetails: (data: Source, className: string) => ReactNode;
};

const scrollToElementWithOffset = (
	element: HTMLElement,
	offset = document.querySelector('#app > header')?.clientHeight ?? 0
) => {
	if (!element) {
		return;
	}

	const target = element.parentElement;

	if (!target) {
		return;
	}

	window.scrollTo({
		top: target.offsetTop - offset,
		behavior: 'smooth'
	});

	target.scrollTo({
		top: element.offsetTop,
		behavior: 'smooth'
	});
};

const TableRow: FC<Readonly<Props>> = ({ data, testId, contentMappers, selected, renderDetails }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(selected);

	const ref = useRef<HTMLDivElement>(null);

	useLayoutEffect(() => {
		if (selected && ref.current) {
			scrollToElementWithOffset(ref.current);
		}
	}, [selected]);

	return (
		<div className={composeClassName(styles.row, open ? styles.rowOpen : '')} ref={ref} data-testid={testId}>
			<ul onClick={() => setOpen(!open)}>
				{Object.keys(contentMappers).map(key => (
					<li key={key}>{contentMappers[key as keyof Source]?.(data)}</li>
				))}

				<li>
					<button className={open ? styles.buttonOpen : undefined} type="button">
						<span className="sr-only">{open ? t('Close table row') : t('Open table row')}</span>
					</button>
				</li>
			</ul>

			{open && renderDetails(data, styles.details)}
		</div>
	);
};

export default TableRow;
